import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type { AggregationDescriptor, MeasureDescriptor } from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { selectReportType } from "../../../../../store/selectors/fieldsStateSelectors";
import { createUiSettingsAction } from "../../../../../store/utils/storeUtils";
import { MetaItemType, ReportType } from "../../../../../../../../../api/types/svTypes.ts";
import AddToChip from "../../../financialStatement/addTo/AddToChip.tsx";
import { isMeasureMeta } from "../../utilities/metaUtils.ts";
import { createMeasureTreeGroups } from "../fieldTree.ts";
import VirtualizedFieldTree from "../VirtualizedFieldTree.tsx";
import { compactFieldItemSx } from "../compactFieldItemSx.ts";
import FieldItemContainer from "../FieldItem.tsx";
import MeasureFieldItem from "./MeasureFieldItem.tsx";
import type { MeasureTreeGroup } from "./buildMeasureSubgroupTree";
import type { MeasureGroupOptionsTarget } from "./MeasureGroupMenu";
import MeasureGroupOptionsIcon from "./MeasureGroupOptionsIcon";

interface Props {
  fields: (MeasureDescriptor | AggregationDescriptor)[];
  groups: MeasureTreeGroup[];
  activeMeasureName?: string;
  onShowOptions: (x: number, y: number, measure: MeasureDescriptor, group: string) => void;
  disabled: boolean;
  expanded: boolean;
  visible: boolean;
  customMeasureGroups: string[];
  onShowGroupOptions?: (x: number, y: number, target: MeasureGroupOptionsTarget) => void;
}

const MeasuresTree = ({
  fields,
  groups,
  activeMeasureName,
  onShowOptions,
  disabled,
  expanded,
  visible,
  customMeasureGroups,
  onShowGroupOptions,
}: Props) => {
  const dispatch = useAppDispatch();
  const reportType = useSelector(selectReportType);
  const handleDragStart = useCallback(() => {
    dispatch(createUiSettingsAction("setDragSessionId", generateGuid()));
  }, [dispatch]);

  const isFinancialStatement = reportType === ReportType.FinancialStatement;
  const showAddTo = isFinancialStatement && !disabled;
  const treeGroups = useMemo(
    () =>
      createMeasureTreeGroups(fields, groups).map((group) => {
        const addTo = showAddTo ? (
          <AddToChip
            source={{
              kind: "measureGroup",
              caption: group.caption,
              measures: [...group.fields, ...group.groups.flatMap((subgroup) => subgroup.fields)].filter(isMeasureMeta),
            }}
          />
        ) : undefined;
        const groupOptions =
          onShowGroupOptions && customMeasureGroups.includes(group.caption) ? (
            <MeasureGroupOptionsIcon onShowOptions={(x, y) => onShowGroupOptions(x, y, { group: group.caption })} />
          ) : undefined;
        return {
          ...group,
          actions:
            addTo || groupOptions ? (
              <>
                {addTo}
                {groupOptions}
              </>
            ) : undefined,
          groups: group.groups.map((subgroup) => {
            const subgroupAddTo = showAddTo ? (
              <AddToChip
                source={{
                  kind: "measureGroup",
                  caption: subgroup.caption,
                  measures: subgroup.fields.filter(isMeasureMeta),
                }}
              />
            ) : undefined;
            const subgroupOptions = onShowGroupOptions ? (
              <MeasureGroupOptionsIcon
                onShowOptions={(x, y) =>
                  onShowGroupOptions(x, y, {
                    group: group.caption,
                    subgroup: { id: subgroup.id, group: group.caption, name: subgroup.caption },
                  })
                }
              />
            ) : undefined;
            return {
              ...subgroup,
              actions:
                subgroupAddTo || subgroupOptions ? (
                  <>
                    {subgroupAddTo}
                    {subgroupOptions}
                  </>
                ) : undefined,
            };
          }),
        };
      }),
    [fields, groups, customMeasureGroups, onShowGroupOptions, showAddTo]
  );
  const renderField = useCallback(
    (field: MeasureDescriptor | AggregationDescriptor, group: string) => (
      <FieldItemContainer
        field={field}
        onDragStart={handleDragStart}
        disabled={disabled}
        active={activeMeasureName === field.name}
        sx={isFinancialStatement ? compactFieldItemSx : undefined}
      >
        <MeasureFieldItem
          field={field}
          disabled={disabled}
          addAction={
            showAddTo && isMeasureMeta(field) ? <AddToChip source={{ kind: "measure", measure: field }} /> : undefined
          }
          onShowOptions={
            field.itemType === MetaItemType.AGGREGATION
              ? undefined
              : (x, y, measure) => onShowOptions(x, y, measure, group)
          }
        />
      </FieldItemContainer>
    ),
    [handleDragStart, disabled, activeMeasureName, onShowOptions, showAddTo, isFinancialStatement]
  );
  return (
    <VirtualizedFieldTree
      key={String(expanded)}
      groups={treeGroups}
      defaultExpanded={expanded}
      visible={visible}
      folderLayout={isFinancialStatement}
      renderField={renderField}
    />
  );
};

export default MeasuresTree;
